import { Panel, secondaryTextClassName } from '@monecolier/ui';
import { CASH_PAYMENT_METHOD_LABELS, type TodayCollectionsResponse } from '@monecolier/api-client';
// UX-56 : même convention que Caisse.tsx/OverdueInvoices.tsx/Fees.tsx —
// `FEE_TYPE_LABELS` (`@monecolier/types`), jamais un second calcul. UX-58 :
// `describeInvoiceFeeTypeWithPeriod` compose désormais AUSSI la période
// (`item.period`, nouveau champ backend) — REMPLACE l'appel direct à
// `FEE_TYPE_LABELS[...]`.
import { describeInvoiceFeeTypeWithPeriod } from '@monecolier/types';

/**
 * Maquette "Accueil Caissier.dc.html" — extrait de `Payments.tsx` (PAY-13,
 * "Mes encaissements du jour") pour être réutilisé aussi par `Home.tsx`
 * (deuxième consommateur réel, pas une extraction préventive). Composant
 * PUREMENT présentationnel — AUCUN appel réseau ici : chaque page reste
 * propriétaire de son propre `useState`/`useEffect`/`getMyCollectionsToday()`
 * (`Payments.tsx` doit pouvoir rafraîchir ce panneau après confirmation d'un
 * paiement, `Home.tsx` n'a besoin que du chargement initial — un état partagé
 * imposerait une coordination inutile entre deux pages indépendantes).
 * `collections === null` = en cours de chargement, jamais confondu avec
 * "aucun encaissement" (`payments.length === 0`).
 */
export function TodayCollectionsPanel({
  collections,
  titleAs,
}: {
  collections: TodayCollectionsResponse | null;
  // UX-74 : deux appelants réels (CashierHome/SchoolAdminHome dans
  // Home.tsx), tous deux sous un <h2> "Bonjour" — h3 passé explicitement
  // par Home.tsx, jamais un niveau fixe en dur ici.
  titleAs?: 'h2' | 'h3' | 'h4';
}) {
  return (
    // UX-21 : icône décorative — `aria-hidden` exclut ce span du nom
    // accessible du titre, qui reste "Mes encaissements du jour" seul.
    <Panel
      title={
        <>
          <span aria-hidden="true">💰</span> Mes encaissements du jour
        </>
      }
      titleAs={titleAs}
      data-testid="payments-today-panel"
    >
      {collections === null ? (
        <p className={secondaryTextClassName} role="status">
          Chargement…
        </p>
      ) : collections.payments.length === 0 ? (
        <p className={secondaryTextClassName} data-testid="payments-today-empty">
          Aucun encaissement aujourd’hui pour le moment.
        </p>
      ) : (
        <>
          <ul className="flex flex-col gap-1" data-testid="payments-today-list">
            {collections.payments.map((item) => (
              <li key={item.id} className={secondaryTextClassName} data-testid={`payments-today-item-${item.id}`}>
                {item.student.lastName} {item.student.firstName} —{' '}
                {item.type === 'INVOICE' ? describeInvoiceFeeTypeWithPeriod(item.feeType ?? '', item.period) : 'Fournitures'} —{' '}
                {item.amount} (
                {CASH_PAYMENT_METHOD_LABELS[item.method]})
              </li>
            ))}
          </ul>
          <p data-testid="payments-today-total">
            <strong>Total : {collections.total}</strong>
          </p>
        </>
      )}
    </Panel>
  );
}
